import InfiniteScroll from 'react-infinite-scroll-component'
import { useEffect } from 'react'

import Header from 'app/DocumentCollectionPage/components/Header'
import TitleSection from 'app/DocumentCollectionPage/components/TitleSection'
import {
  DEFAULT_CATEGORY_TITLE,
  DEFAULT_CATEGORY_DESCRIPTION,
  DEFAULT_CATEGORY_THUMBNAIL,
  HELPDESK_LINK,
} from 'app/DocumentCollectionPage/constants'
import useCategories from 'app/DocumentCollectionPage/hooks/useCategories'
import CategoryError from 'app/DocumentCollectionPage/components/CategoryError'
import CategoryListItem from 'app/DocumentCollectionPage/components/CategoryListItem'
import DocumentEmptyState from 'app/DocumentCollectionPage/components/DocumentEmptyState'
import {
  CategoryListPreloader,
  CategoryListItemPreloader,
} from 'app/DocumentCollectionPage/components/Preloader'
import { PageContainer, DesktopPageHeader } from 'components/ResponsiveLayout'
import { useDocumentDesktopView } from '../hooks/useDocumentDesktopView'
import { useSETrackEvent } from '../hooks/useSETrackEvent'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

const LandingPage = () => {
  const { SETrackDocumentLandingPage } = useSETrackEvent()
  const [, setValue] = useLocalStorage('categoryLvl', null)
  const { isDesktop } = useDocumentDesktopView()
  const {
    fetchNextPage,
    hasCategoriesData,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    categoriesData,
    categoriesMeta,
    lastPageNumber,
    refetch,
  } = useCategories()

  const retryHandler = () => {
    refetch({ refetchPage: (page, index) => index === lastPageNumber })
  }

  useEffect(() => {
    setValue(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      !isDesktop ||
      !isSuccess ||
      isLoading ||
      !hasCategoriesData ||
      !hasNextPage ||
      isFetchingNextPage ||
      categoriesMeta?.isNextPageError
    ) {
      return
    }

    const isPageScrollable =
      document.documentElement.scrollHeight > window.innerHeight

    if (!isPageScrollable) {
      fetchNextPage()
    }
  }, [
    categoriesData.length,
    categoriesMeta?.isNextPageError,
    fetchNextPage,
    hasCategoriesData,
    hasNextPage,
    isDesktop,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  ])

  const categoriesList = (
    <>
      {isLoading && <CategoryListPreloader isDesktop={isDesktop} />}
      {isSuccess && (
        <>
          {hasCategoriesData ? (
            <InfiniteScroll
              dataLength={categoriesData.length}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                categoriesMeta?.isNextPageError ? (
                  <CategoryError onClick={retryHandler} />
                ) : (
                  <CategoryListItemPreloader />
                )
              }
              endMessage={
                categoriesMeta?.isNextPageError ? (
                  <CategoryError onClick={retryHandler} />
                ) : null
              }
            >
              <ul
                className={`pb-2 ${
                  isDesktop ? 'space-y-0 grid grid-cols-2 gap-4' : 'space-y-4'
                }`}
              >
                {categoriesData.map((item, index) => (
                  <CategoryListItem
                    key={`category-${item?.id ?? index}`}
                    {...item}
                    onNextPage={() => setValue(1)}
                  />
                ))}
              </ul>
            </InfiniteScroll>
          ) : (
            <DocumentEmptyState />
          )}
        </>
      )}
    </>
  )

  if (isDesktop) {
    return (
      <div className="min-h-screen bg-surface-subdued">
        <DesktopPageHeader
          breadcrumbItems={[{ label: 'Dokumen Rujukan', href: null }]}
          navLinks={[{ label: 'Bantuan', href: HELPDESK_LINK, external: true }]}
        />
        <main className="bg-white">
          <PageContainer>
            <TitleSection
              title={DEFAULT_CATEGORY_TITLE}
              description={DEFAULT_CATEGORY_DESCRIPTION}
              thumbnail={DEFAULT_CATEGORY_THUMBNAIL}
            />
          </PageContainer>
          <div className="bg-surface-subdued w-full py-2">
            <PageContainer>
              <section className="p-4 py-4">{categoriesList}</section>
            </PageContainer>
          </div>
        </main>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-xl mx-auto">
        <Header
          showHelpButton
          headerRightOnClick={() =>
            SETrackDocumentLandingPage('CLICK_HELP_KUMPULAN_DOKUMEN', {
              screen_name: 'KUMPULAN_DOKUMEN_LANDING_PAGE',
              event_trigger: 'KUMPULAN_DOKUMEN_LANDING_PAGE',
              url: HELPDESK_LINK,
            })
          }
        />

        <main className="mt-16">
          <TitleSection
            title={DEFAULT_CATEGORY_TITLE}
            description={DEFAULT_CATEGORY_DESCRIPTION}
            thumbnail={DEFAULT_CATEGORY_THUMBNAIL}
          />

          <section className="min-h-screen p-4 bg-default">
            {categoriesList}
          </section>
        </main>
      </div>
    </>
  )
}

export default LandingPage
